import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useBatchContext } from '@/app/BatchContext';
import {
  AsyncStateBoundary,
  BLOCKER_CATEGORY_SECTIONS,
  isBlockerCategory,
  resolveIssueNavigation,
  type IssueNavigationIntent,
} from '@/features/audit-issues';
import { BlockerCategoryCard } from './BlockerCategoryCard';
import { listPathForCategory } from './listPathForCategory';
import { useBlockers } from './useBlockers';
import type { BlockerBatch } from './types';

/**
 * Most direct safe navigation intent for a category's affected batch
 * (spec-13 §8.3): the two exception-count categories open the batch's
 * exception list; every other category opens its nearest mapped section
 * (`BLOCKER_CATEGORY_SECTIONS`), falling back to the plain batch page when a
 * category has no more specific section.
 */
const intentForCategory = (category: string, batchId: string): IssueNavigationIntent => {
  if (category === 'open_critical_exceptions' || category === 'open_error_exceptions') {
    return { kind: 'batch_exceptions', params: { batch_id: batchId } };
  }
  const section = isBlockerCategory(category) ? BLOCKER_CATEGORY_SECTIONS[category] : null;
  return section
    ? { kind: 'monthly_batch', params: { batch_id: batchId, section } }
    : { kind: 'monthly_batch', params: { batch_id: batchId } };
};

/** Filtered "open the full list" destination for a category (spec-13 §8.3). */

/**
 * FR-016 blocker dashboard: a cross-batch view of what is blocking close,
 * scoped + supplier-variant-filtered by the backend. Each category drills
 * down to its sampled affected batches (spec-13 §8.3).
 */
const BlockerDashboardPage = () => {
  const { t } = useTranslation(['issues', 'common']);
  const navigate = useNavigate();
  const { setBatchId } = useBatchContext();
  const query = useBlockers();

  const resolveBatchPath = (batch: BlockerBatch, category: string): string => {
    const resolved = resolveIssueNavigation(
      intentForCategory(category, batch.monthly_batch_id),
      batch.monthly_batch_id,
    );
    // Never '/': resolveIssueNavigation degrades to the batch page at worst.
    return resolved.ok ? resolved.path : (resolved.fallbackPath ?? '/batches');
  };

  const openBatch = (batch: BlockerBatch, category: string) => {
    setBatchId(batch.monthly_batch_id);
    navigate(resolveBatchPath(batch, category));
  };

  return (
    <div className="space-y-4 p-6">
      <h1 className="text-lg font-semibold text-efs-text-primary">
        {t('efs.dashboard.blockers.title')}
      </h1>

      <AsyncStateBoundary
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isFetching={query.isFetching}
        data={query.data}
        onRetry={() => query.refetch()}
        isEmpty={(data) => data.blockers.every((b) => b.count === 0)}
        emptyContent={
          <p className="text-sm text-efs-text-muted">{t('efs.dashboard.blockers.none')}</p>
        }
      >
        {(data) => {
          const sorted = [...data.blockers].sort((a, b) => b.count - a.count);
          return (
            <div className="space-y-4">
              <p className="text-sm text-efs-text-muted">
                {t('efs.dashboard.blockers.open_batches', { count: data.open_batches })}
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {sorted.map((blocker) => (
                  <BlockerCategoryCard
                    key={blocker.category}
                    category={blocker.category}
                    count={blocker.count}
                    batches={blocker.batches}
                    pathForBatch={(batch) => resolveBatchPath(batch, blocker.category)}
                    onOpenBatch={(batch) => openBatch(batch, blocker.category)}
                    onOpenList={() => navigate(listPathForCategory(blocker.category))}
                  />
                ))}
              </div>
            </div>
          );
        }}
      </AsyncStateBoundary>
    </div>
  );
};

export default BlockerDashboardPage;
